import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, AlertTriangle, Clock, MapPin, ChevronDown } from 'lucide-react';
import { DataCollectionForm } from './DataCollectionForm';
export type CheckStatus = 'passed' | 'failed' | 'warning' | 'pending';
interface ComplianceCheckProps {
  title: string;
  description: string;
  status: CheckStatus;
  remediation?: string;
  location?: {
    section: string;
    excerpt?: string;
  };
  pendingData?: {
    type: 'nmls' | 'rateSheet' | 'stateLicenses';
    detectedValue?: string;
  };
  onDataSubmit?: (value: string) => void;
}
export function ComplianceCheck({
  title,
  description,
  status,
  remediation,
  location,
  pendingData,
  onDataSubmit
}: ComplianceCheckProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localStatus, setLocalStatus] = useState(status);
  const statusConfig = {
    passed: {
      icon: Check,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      titleColor: 'text-slate-900',
      descColor: 'text-slate-600'
    },
    failed: {
      icon: X,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-red-900',
      descColor: 'text-red-700'
    },
    warning: {
      icon: AlertTriangle,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-900',
      descColor: 'text-amber-700'
    },
    pending: {
      icon: Clock,
      iconBg: 'bg-slate-200',
      iconColor: 'text-slate-600',
      titleColor: 'text-slate-900',
      descColor: 'text-slate-600'
    }
  };
  const config = statusConfig[localStatus];
  const Icon = config.icon;
  const hasDetails = localStatus !== 'passed' && (remediation || location || pendingData);
  const getSummary = () => {
    if (localStatus === 'pending') return 'Setup required to verify';
    if (localStatus === 'failed') return remediation?.split('.')[0] || 'Action required';
    if (localStatus === 'warning') return remediation?.split('.')[0] || 'Review recommended';
    return description;
  };
  const handleDataSubmit = (value: string) => {
    if (onDataSubmit) {
      onDataSubmit(value);
      // Simulate check completion - in real app, this would come from API
      setLocalStatus('passed');
      setIsExpanded(false);
    }
  };
  return <div className="py-4 px-4 border-b border-slate-100 last:border-0" data-id="element-20">
      <div className="flex items-start gap-4" data-id="element-21">
        <div className="flex-shrink-0 mt-0.5" data-id="element-22">
          <div className={`w-6 h-6 rounded-full ${config.iconBg} flex items-center justify-center ${config.iconColor}`} data-id="element-23">
            <Icon className="w-4 h-4" strokeWidth={2.5} data-id="element-24" />
          </div>
        </div>

        <div className="flex-1 min-w-0" data-id="element-25">
          {/* Title and Summary */}
          <div className="flex items-start justify-between gap-3 mb-1" data-id="element-26">
            <div className="flex-1" data-id="element-27">
              <h4 className={`text-sm font-semibold ${config.titleColor}`} data-id="element-28">
                {title}
              </h4>
              {!isExpanded && <p className={`text-sm ${config.descColor} mt-1`} data-id="element-29">
                  {localStatus === 'passed' ? description : getSummary()}
                </p>}
            </div>

            {hasDetails && <button onClick={() => setIsExpanded(!isExpanded)} className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${localStatus === 'pending' ? 'text-slate-700 hover:bg-slate-100' : localStatus === 'failed' ? 'text-red-700 hover:bg-red-50' : 'text-amber-700 hover:bg-amber-50'}`} data-id="element-30">
                <span data-id="element-31">
                  {isExpanded ? 'Hide' : localStatus === 'pending' ? 'Setup' : 'Details'}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-32" />
              </button>}
          </div>

          {/* Expanded Details */}
          <AnimatePresence data-id="element-33">
            {isExpanded && hasDetails && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className="overflow-hidden" data-id="element-34">
                <div className="pt-3 space-y-3" data-id="element-35">
                  {/* Full Description */}
                  <p className={`text-sm ${config.descColor} leading-relaxed`} data-id="element-36">
                    {description}
                  </p>

                  {/* Pending Data Collection */}
                  {localStatus === 'pending' && pendingData && <DataCollectionForm type={pendingData.type} detectedValue={pendingData.detectedValue} onSubmit={handleDataSubmit} onSkip={() => setIsExpanded(false)} data-id="element-37" />}

                  {/* Location (for failed/warning) */}
                  {localStatus !== 'pending' && location && <div className="space-y-2" data-id="element-38">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500" data-id="element-39">
                        <MapPin className="w-3.5 h-3.5" data-id="element-40" />
                        <span data-id="element-41">
                          Found in:{' '}
                          <span className="font-medium text-slate-700" data-id="element-42">
                            {location.section}
                          </span>
                        </span>
                      </div>

                      {location.excerpt && <div className="bg-slate-900 rounded-md p-3 border border-slate-700" data-id="element-43">
                          <pre className="text-xs text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap" data-id="element-44">
                            {location.excerpt}
                          </pre>
                        </div>}
                    </div>}

                  {/* Remediation (for failed/warning) */}
                  {localStatus !== 'pending' && remediation && <div className={`p-3 rounded-lg border ${localStatus === 'failed' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-45">
                      <p className={`text-xs font-medium ${localStatus === 'failed' ? 'text-red-900' : 'text-amber-900'} mb-1`} data-id="element-46">
                        {localStatus === 'failed' ? '⚠️ Action Required' : '⚡ Recommendation'}
                      </p>
                      <p className={`text-xs ${localStatus === 'failed' ? 'text-red-700' : 'text-amber-700'}`} data-id="element-47">
                        {remediation}
                      </p>
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}